/**
 * The M6 station panels' feature (plan P27): in the Reach with a career, registers the Shipyard, Hangar and Captain
 * roster tabs (`src/ui/fleet/panels.tsx`) into the station services registry once. The panels find the `fleet`
 * service (P21, `fleetLink`, order strategic + 2) when they are listed and mounted, so a link that is not up yet only
 * hides them.
 *
 * The Hangar's switch, ferry and transfer go through the fleet service's P24 moves (`switchToShip`, `ferryShip`,
 * `transferShip`), wired below.
 */
import { FEATURE_ORDER, type GameContext, type GameFeature } from '../../app/feature'
import { powerBudget, powerDraw } from '../../career/fitting'
import type { CareerService } from '../../career/flow'
import type { Career } from '../../career/types'
import type { FleetService } from '../../fleet-link/service'
import type { StationServiceRegistry } from '../../reach/ports'
import type { M2Runtime } from '../../space/contracts'
import { createFleetStationPanels, createHangarActions } from '../../ui/fleet/panels'
import { transfersEnabled } from '../../fleet-link/transfer'
import { createTransferPanel } from '../../ui/fleet/Transfers'

/** After the fleet link (strategic + 2). */
const ORDER = FEATURE_ORDER.strategic + 3

const registered = new WeakSet<StationServiceRegistry>()

const NO_FLEET = Promise.resolve({ ok: false, error: 'noFleet' } as const)

function hasWriteHook(m2: M2Runtime): m2 is CareerService {
  return 'applyDevCommand' in m2 && typeof m2.applyDevCommand === 'function'
}

const feature: GameFeature = {
  name: 'fleetPanels',
  order: ORDER,
  init(ctx: GameContext): void {
    const reach = ctx.reach
    if (reach === null || registered.has(reach.services)) return
    const client = ctx.services.maybe('strategic')
    const m2 = ctx.services.maybe('m2')
    if (client === undefined || m2 === undefined) return
    registered.add(reach.services)
    const fleet = (): FleetService | null => ctx.services.maybe('fleet') ?? null
    const stock = m2.data.career.start.fitted
    const fit = { draw: powerDraw(stock, m2.data), budget: powerBudget(stock, m2.data) }
    const systemNames = new Map(reach.galaxy.systems.map((s) => [s.id, s.name]))
    const panels = createFleetStationPanels({
      fleet,
      client,
      career: (): Career | null => {
        try {
          return m2.career()
        } catch {
          return null
        }
      },
      stockFit: () => fit,
      // M10 P5a3a: the compare panel reads the module data and the start fit; P5a2a: the Pin writes the Career.
      compare: { modules: m2.data, startFit: stock },
      ...(hasWriteHook(m2) ? { applyCareer: (name: string, cmd: (c: Career) => Career) => m2.applyDevCommand(name, cmd) } : {}),
      hangar: createHangarActions(fleet, {
        // The Hangar offers a switch only for ships docked here, so the ship's own station is where the player is.
        switchTo: async (shipId) => {
          const f = fleet()
          if (f === null) return NO_FLEET
          const ship = (await f.fleetView()).ships.find((s) => s.id === shipId)
          if (ship?.stationId == null) return { ok: false, error: 'notDocked' }
          return f.switchToShip(shipId, ship.stationId)
        },
        ferry: (shipId, stationId) => fleet()?.ferryShip(shipId, stationId) ?? NO_FLEET,
        transfer: (shipId, stationId) => fleet()?.transferShip(shipId, stationId) ?? NO_FLEET,
      }),
      placeName: (ship) => {
        if (ship.stationId !== null) {
          const local = client.systemView?.stations.find((s) => s.id === ship.stationId)
          if (local !== undefined) return local.name
        }
        return ship.systemId === null ? '' : (systemNames.get(ship.systemId) ?? ship.systemId)
      },
    })
    for (const p of panels) reach.services.register(p)
    // M10 P5a8b: the transfer panel, only with `moor.json transfers.enabled` on (false until W6) and a Career writer.
    if (transfersEnabled() && hasWriteHook(m2)) {
      reach.services.register(
        createTransferPanel({
          fleet,
          career: (): Career | null => {
            try {
              return m2.career()
            } catch {
              return null
            }
          },
          applyCareer: (name, cmd) => m2.applyDevCommand(name, cmd),
          modules: m2.data,
        }),
      )
    }
  },
}

export default feature
